import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  TrendingUp, 
  BookOpen, 
  Zap,
  MessageSquare,
  UsersRound,
  Handshake,
  GraduationCap,
  Briefcase,
  Newspaper,
  Calendar,
  UserSearch,
  Gift,
  Share2,
  ArrowRight,
  CheckCircle,
  Bot,
  Globe,
  DollarSign,
  Workflow,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Star
} from 'lucide-react';
import '../styles/moxey-cards.css';

const LOGO_URL = "/images/area-logo.png";

const whyAreaCards = [
  {
    icon: Users,
    title: "Collaborate",
    description: "Build meaningful relationships with agents, lenders, title partners, inspectors, photographers, and other industry professionals."
  },
  {
    icon: TrendingUp,
    title: "Grow",
    description: "Access referrals, partnerships, jobs, events, and new business opportunities."
  },
  {
    icon: BookOpen,
    title: "Improve",
    description: "Learn through education, courses, industry insight, and professional development."
  },
  {
    icon: Zap,
    title: "Execute",
    description: "Gain discounted access to tools like Moxey OS to help run your business more efficiently."
  }
];

const communityCards = [
  {
    icon: MessageSquare,
    title: "Share ideas and opportunities",
    description: "Post updates, insights, and business opportunities with the network."
  },
  {
    icon: UsersRound,
    title: "Join focused collaboration groups",
    description: "Connect with professionals by market, specialty, or focus area."
  },
  {
    icon: Handshake,
    title: "Build stronger business relationships",
    description: "Turn connections into referrals, partnerships, and growth."
  }
];

const benefitItems = [
  { icon: Users, title: "Community access", description: "Connect with a private network of professionals" },
  { icon: GraduationCap, title: "Education and courses", description: "Learn and improve through professional development" },
  { icon: Briefcase, title: "Job board", description: "Discover career and business opportunities" },
  { icon: Newspaper, title: "News", description: "Stay informed with industry updates" },
  { icon: UserSearch, title: "Member directory", description: "Find agents, partners, and service providers" },
  { icon: Share2, title: "Referral opportunities", description: "Share and receive trusted referrals" },
  { icon: Gift, title: "Partner discounts", description: "Exclusive savings from trusted partners" },
  { icon: Zap, title: "Moxey OS discounts", description: "Exclusive member pricing on AI business tools" },
  { icon: DollarSign, title: "Affiliate program", description: "Earn by growing the alliance" }
];

const moxeyModules = [
  {
    title: "Moxey Flow",
    tag: "Workflow",
    description: "Your core workflow hub for managing contacts, leads, transactions, commissions, reporting, and direct deposit.",
    features: ["CRM", "Transaction Management", "Commission Tracking", "Direct Deposit", "E-Signatures", "Reporting"],
    badge: "/logos/moxey-flow-badge.png",
    cardClass: "flow-card",
  },
  {
    title: "Moxey AI",
    tag: "Automation",
    description: "Your AI Business Leader for execution, insights, automation, and growth.",
    features: ["Lead Generation", "Executive Assistance", "Email Marketing", "Content Creation", "Analytics & Strategy"],
    badge: "/logos/moxey-ai-badge.png",
    cardClass: "ai-card",
  },
  {
    title: "Moxey EMD",
    tag: "Deposits",
    description: "Manage earnest money deposits with a grounded, professional workflow.",
    features: ["Deposit Tracking", "Routing", "Status Monitoring", "Confirmations", "Alerts"],
    badge: "/logos/moxey-emd-badge.png",
    cardClass: "emd-card",
  },
  {
    title: "Moxey PMS",
    tag: "Property Ops",
    description: "Property and short-term rental operations in one premium control center.",
    features: ["Property Tracking", "Calendars", "Cleaning Schedules", "Owner Reporting", "Revenue Overview"],
    badge: "/logos/moxey-pms-badge.png",
    cardClass: "pms-card",
  },
];

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeColorMap = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700' };

function PartnerSpotlight() {
  const [partners, setPartners] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/partners/featured`).then(r => r.json()).then(d => setPartners(d.partners || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (partners.length <= 1) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % partners.length), 5000);
    return () => clearInterval(timerRef.current);
  }, [partners.length]);

  const go = (dir) => {
    clearInterval(timerRef.current);
    setIdx(i => (i + dir + partners.length) % partners.length);
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % partners.length), 5000);
  };

  if (partners.length === 0) return null;
  const p = partners[idx];

  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid="partner-spotlight-section">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Star className="w-5 h-5 text-amber-500" />
            <p className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase">Partner Spotlight</p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
            Featured Partners & Member Benefits
          </h2>
        </div>

        <div className="relative">
          {partners.length > 1 && (
            <>
              <button onClick={() => go(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="spotlight-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => go(1)} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="spotlight-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
            </>
          )}

          <Card className="border-0 shadow-lg overflow-hidden" data-testid={`spotlight-card-${p.slug}`}>
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-48 bg-[#0f1f33] flex items-center justify-center p-8">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="w-20 h-20 object-contain" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center">
                      <Star className="w-10 h-10 text-amber-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 p-6 sm:p-8">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                    {p.badges?.map((b, i) => (
                      <Badge key={i} className={`text-[10px] ${badgeColorMap[b.color_theme] || 'bg-gray-100 text-gray-600'}`}>{b.label}</Badge>
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{p.short_description}</p>
                  {(p.discount_text || p.coupon_code) && (
                    <div className={`rounded-lg p-3 mb-4 inline-block ${p.is_exclusive_discount ? 'bg-rose-50 border border-rose-100' : 'bg-emerald-50 border border-emerald-100'}`}>
                      {p.discount_text && <p className="text-sm font-semibold text-slate-800">{p.discount_text}</p>}
                      {p.coupon_code && <p className="text-xs text-slate-500 mt-0.5">Code: <span className="font-mono font-bold text-slate-800">{p.coupon_code}</span></p>}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Link to={`/partners/${p.slug}`}>
                      <Button variant="outline" size="sm" className="text-xs">View Details</Button>
                    </Link>
                    {p.affiliate_url && (
                      <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-[#c41230] hover:bg-[#a00f28] text-white text-xs">
                          {p.primary_cta_text || 'Visit Partner'} <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dots */}
          {partners.length > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {partners.map((_, i) => (
                <button key={i} onClick={() => { clearInterval(timerRef.current); setIdx(i); timerRef.current = setInterval(() => setIdx(j => (j + 1) % partners.length), 5000); }}
                  className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-[#3D4F7A] w-6' : 'bg-slate-300 hover:bg-slate-400'}`}
                  data-testid={`spotlight-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <Link to="/partners">
            <Button variant="outline" className="text-sm">
              View All Partners <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate('/dashboard', { replace: true });
  }, [user, loading, navigate]);

  if (loading || user) return null;

  return (
    <div className="bg-white" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/hero-team.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
              American Real Estate Alliance
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              A business alliance built for collaboration, growth, and shared success.
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Connect with agents, lenders, title partners, inspectors, photographers, and other professionals. 
              Share opportunities, build relationships, access education, and grow your business through a stronger network.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                  Join for FREE today!
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Commission Transparency Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="commission-transparency-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
            FREE to Licensed Real Estate Brokers & Agents
          </p>
          <img src="/images/split-report-logo.png" alt="Split Report — Transparent RE Commission Splits" className="h-28 sm:h-36 mx-auto mb-4" data-testid="split-report-logo" />
          <p className="text-sm sm:text-base text-slate-600 mb-10 w-full mx-auto leading-relaxed text-center">
            The free, membership-based database where licensed real estate agents share and view sales commission splits.<br />
            Promote transparency and collaboration in your market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/register">
              <Button size="lg" className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                Join for FREE today!
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" />No credit card required — ever</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" />Agent data is never sold</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" />Free market snapshots</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-slate-600 mt-3">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" />Free iOS &amp; Android App (coming soon)</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" />Permanent listing history for transparency</span>
          </div>
        </div>
      </section>

      {/* Why AREA Section */}
      <section className="py-20 md:py-28 bg-white" id="why-area">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
              Why AREA
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for professionals who want more
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              AREA is designed to help real estate professionals collaborate, grow, improve, and execute at a higher level.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyAreaCards.map((card, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#3D4F7A]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#3D4F7A] transition-colors">
                    <card.icon className="w-7 h-7 text-[#3D4F7A] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{card.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="relative overflow-hidden" id="community">
        <div className="absolute inset-0">
          <img src="/images/community-preview.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1a2744]/90"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#60a5fa] font-semibold text-sm tracking-wider uppercase mb-3">
                Community
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                The collaboration hub of the alliance
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Share ideas, opportunities, wins, insights, photos, and videos. Join groups by market, 
                specialty, and focus, and build relationships that lead to growth.
              </p>
            </div>
            <div className="space-y-6">
              {communityCards.map((card, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <card.icon className="w-6 h-6 text-[#60a5fa]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{card.title}</h3>
                    <p className="text-slate-400 text-sm">{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-white" id="benefits">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
              Member Benefits
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything you need to grow
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              AREA membership unlocks access to tools, resources, and opportunities designed to help you succeed.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefitItems.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-6 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className="w-10 h-10 bg-[#3D4F7A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#3D4F7A]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/register">
              <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                Join for FREE today!
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partner Spotlight Section */}
      <PartnerSpotlight />

      {/* Moxey OS Section */}
      <section className="py-20 md:py-28" style={{ background: '#0b0f1a' }} id="moxey-os" data-testid="moxey-os-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
              Member Advantage
            </p>
            <img src="/logos/moxey-os-badge.png" alt="Moxey OS" className="w-40 h-40 sm:w-48 sm:h-48 mx-auto mb-6 rounded-2xl" style={{ boxShadow: '0 10px 40px rgba(37, 99, 235, 0.3)' }} data-testid="moxey-os-logo" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              A smarter way to operate with Moxey OS
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
              AREA members receive discounted access to Moxey OS, the AI operating system for business.
              Moxey helps professionals generate leads, manage relationships, automate follow-up,
              handle transactions, power their websites, and manage commissions more efficiently.
            </p>
          </div>
          <div className="moxey-module-grid">
            {moxeyModules.map((mod, index) => (
              <div key={index} className={`moxey-card ${mod.cardClass}`} data-testid={`moxey-card-${mod.cardClass}`}>
                <div className="card-glow"></div>
                <div className="card-top">
                  <img src={mod.badge} alt={mod.title} className="module-badge" />
                  <div className="card-title-wrap">
                    <h3>{mod.title}</h3>
                    <span className="module-tag">{mod.tag}</span>
                  </div>
                </div>
                <p className="card-description">{mod.description}</p>
                <ul className="feature-list">
                  {mod.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <div className="card-footer">
                  <span>Explore Module</span>
                  <span className="arrow">&rarr;</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <a href="https://www.moxeyos.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                Explore Moxey OS
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Affiliate / Growth Section */}
      <section className="py-20 md:py-28 bg-white" id="affiliate">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#D42B3C] font-semibold text-xl sm:text-2xl tracking-wider uppercase mb-4">
              Affiliate Program
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Turn relationships into opportunity
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Members can grow through referrals, strategic partnerships, and an affiliate program 
              that rewards them for helping expand the alliance. When the network grows, everyone benefits.
            </p>
            <Link to="/register">
              <Button className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                Join for FREE today!
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 bg-[#3D4F7A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              Join the alliance
            </h2>
            <p className="text-lg text-slate-200 mb-10 max-w-2xl mx-auto leading-relaxed">
              Become part of a growing network built to help real estate professionals 
              collaborate, improve, and succeed together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white text-[#3D4F7A] hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
                  Join for FREE today!
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
